import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Chip, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'

const CONTRACT_LABELS = {
  OwnershipContract: 'Eigentum',
  LeaseContract: 'Miete',
  Vacancy: 'Leerstand',
}

const CONTRACT_COLORS = {
  OwnershipContract: 'primary',
  LeaseContract: 'secondary',
  Vacancy: 'default',
}

Index.layout = (page) => <AppShell>{page}</AppShell>

export default function Index({ property, contracts }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')

  const filtered = contracts.filter((c) => {
    if (typeFilter !== 'all' && c.type !== typeFilter) return false
    if (statusFilter === 'active' && !c.active) return false
    if (statusFilter === 'ended' && c.active) return false
    return true
  })

  // Group by unit
  const grouped = {}
  filtered.forEach((c) => {
    const key = c.unit_number
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(c)
  })

  return (
    <Box>
      <PageHeader
        title="Verträge"
        breadcrumbs={[{ label: 'Verträge' }]}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/contracts/new`)}
            data-testid="new-contract-btn"
          >
            Neuer Vertrag
          </Button>
        }
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Typ</InputLabel>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            label="Typ"
            data-testid="type-filter"
          >
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value="OwnershipContract">Eigentum</MenuItem>
            <MenuItem value="LeaseContract">Miete</MenuItem>
            <MenuItem value="Vacancy">Leerstand</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
            data-testid="status-filter"
          >
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value="active">Aktiv</MenuItem>
            <MenuItem value="ended">Beendet</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ fontWeight: 600 }}>Einheit</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Vertrag</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Typ</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Kontakt(e)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Von</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Bis</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(grouped).length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Keine Verträge vorhanden.</Typography>
                </TableCell>
              </TableRow>
            )}
            {Object.entries(grouped).map(([unitNumber, unitContracts]) =>
              unitContracts.map((contract, idx) => (
                <TableRow
                  key={contract.id}
                  hover
                  sx={{ cursor: 'pointer', opacity: contract.active ? 1 : 0.5 }}
                  onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/contracts/${contract.id}`)}
                  data-testid={`contract-row-${contract.id}`}
                >
                  <TableCell>
                    {idx === 0 ? (
                      <Typography variant="body2" fontWeight={500}>{unitNumber}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{contract.number || '–'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={CONTRACT_LABELS[contract.type] || contract.type}
                      color={CONTRACT_COLORS[contract.type] || 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{contract.contact_name || '–'}</TableCell>
                  <TableCell>{contract.start_date}</TableCell>
                  <TableCell>{contract.end_date || '–'}</TableCell>
                  <TableCell>
                    <Chip
                      label={contract.active ? 'Aktiv' : 'Beendet'}
                      color={contract.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
